import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { FeedbackType, FeedbackStatus } from "@/integrations/supabase/types";
import { toast } from "@/hooks/use-toast";

export interface AdminFeedbackItem {
  id: string;
  user_id: string;
  type: FeedbackType;
  title: string;
  description: string | null;
  page_url: string | null;
  user_agent: string | null;
  status: FeedbackStatus;
  admin_notes: string | null;
  admin_reply: string | null;
  replied_at: string | null;
  resolved_at: string | null;
  webhook_sent_at: string | null;
  created_at: string;
  updated_at: string;
  user_name: string | null;
  user_email: string | null;
}

interface AdminFeedbackParams {
  type?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export function useAdminFeedback(params: AdminFeedbackParams = {}) {
  const { type, status, page = 1, limit = 20 } = params;

  return useQuery<{ items: AdminFeedbackItem[]; total: number }>({
    queryKey: ["admin-feedback", type, status, page, limit],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: { action: "list_feedback", type, status, page, limit },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data as { items: AdminFeedbackItem[]; total: number };
    },
    staleTime: 30 * 1000,
  });
}

export function useUpdateFeedbackStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      feedbackId: string;
      status: FeedbackStatus;
      adminNotes?: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: {
          action: "update_feedback_status",
          feedback_id: params.feedbackId,
          status: params.status,
          admin_notes: params.adminNotes,
        },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Feedback atualizado" });
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao atualizar feedback", variant: "destructive" });
    },
  });
}

export function useReplyFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { feedbackId: string; reply: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: { action: "reply_feedback", feedback_id: params.feedbackId, reply: params.reply },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Resposta enviada por email ao usuário" });
      queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao enviar resposta", variant: "destructive" });
    },
  });
}

export function useFeedbackStats() {
  return useQuery({
    queryKey: ["admin-feedback-stats"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: { action: "get_feedback_stats" },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error);
      return data.data as {
        total: number;
        openCount: number;
        resolvedCount: number;
        closedCount: number;
        byType: Record<string, number>;
        byStatus: Record<string, number>;
      };
    },
    staleTime: 60 * 1000,
  });
}
